import { Component, Input, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { COMMON_IMPORTS } from "../../global-misc/common-imports";
import { VisitsViewFacade } from "./visits-view.facade";
import { CardModule } from "primeng/card";
import { TagModule } from "primeng/tag";
import { AccordionModule } from "primeng/accordion";
import { MenuModule } from "primeng/menu";
import { MenuItem } from "primeng/api";
import { Eye, EyeMeasurementModel } from "../../global-data-models/visit.model";
import { SignedNumberPipe } from "../../global-misc/signed-number.pipe";
import { VisitWithAgeModel } from "./visits-view.facade";
import { DividerModule } from "primeng/divider";
import { VisitService } from "../../global-services/visit.service";
@Component({
    selector: "app-visits-view",
    standalone: true,
    imports: [...COMMON_IMPORTS, CardModule, TagModule, AccordionModule, MenuModule, SignedNumberPipe, DividerModule],
    templateUrl: "./visits-view.component.html",
    styleUrls: ["./visits-view.component.css"],
})
export class VisitsViewComponent implements OnInit {
    public readonly Eye = Eye;

    @Input() patientId!: string;
    @Input() orgId!: string;
    @Input() collapsible: boolean = false;

    public accordionActiveIndex: number | null = null;
    public visits: VisitWithAgeModel[] = [];

    public constructor(
        private readonly facade: VisitsViewFacade,
        private readonly router: Router,
        private readonly visitService: VisitService
    ) { }

    public async ngOnInit() {
        await this.refreshVisits();
    }

    public async refreshVisits() {
        this.visits = await this.facade.getVisitsWithAge(this.orgId, this.patientId);
    }

    public getHumanReadableDate(dateString: string): string {
        return this.facade.getHumanReadableDate(new Date(dateString));
    }

    public getMeasurementSeverity(measurement: EyeMeasurementModel): "info" | "warning" | "secondary" {
        if (measurement.sphere === undefined || measurement.sphere === null) {
            return "secondary";
        }

        const cylinderValue = measurement.cylinder || 0;
        const sphericalEquivalent = measurement.sphere + (0.5 * cylinderValue);

        return sphericalEquivalent <= -0.5 ? 'warning' : 'info';
    }

    public getMeasurementLabel(measurement: EyeMeasurementModel): string {
        if (measurement.sphere === undefined || measurement.sphere === null) {
            return "No Classification";
        }

        const cylinderValue = measurement.cylinder || 0;
        const sphericalEquivalent = measurement.sphere + (0.5 * cylinderValue);

        return sphericalEquivalent <= -0.5 ? 'Myopic' : 'Pre-myopic';
    }

    public getLatestVisit(): VisitWithAgeModel | null {
        if (this.visits.length === 0) return null;

        return this.visits.reduce((latest, current) => {
            return new Date(current.date) > new Date(latest.date) ? current : latest;
        });
    }

    public getVisitMenuItems(visit: VisitWithAgeModel): MenuItem[] {
        return [
            {
                label: 'Update Visit',
                icon: 'pi pi-pencil',
                command: () => this.navigateToUpdateVisit(visit.id)
            },
            {
                label: 'Delete Visit',
                icon: 'pi pi-trash',
                command: () => this.deleteVisit(visit.id)
            }
        ];
    }

    public navigateToUpdateVisit(visitId: string): void {
        this.router.navigate(['/orgs', this.orgId, 'patients', this.patientId, 'visit', visitId, 'update']);
    }

    public async deleteVisit(visitId: string) {
        // TODO: Implement delete functionality
        await this.visitService.deletePatientVisit(this.orgId, this.patientId, visitId);
        this.refreshVisits();
    }

    public getSortedEyeMeasurements(eyeMeasurements: EyeMeasurementModel[]): EyeMeasurementModel[] {
        return [...eyeMeasurements].sort((a, b) => {
            // RIGHT eye comes first, then LEFT eye
            if (a.eyeSide === Eye.RIGHT && b.eyeSide === Eye.LEFT) return -1;
            if (a.eyeSide === Eye.LEFT && b.eyeSide === Eye.RIGHT) return 1;
            return 0;
        });
    }
}